import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { EmptyList } from '@components/EmptyList';
import { Anchor } from '@components/ui/anchor';
import { Pagination } from '@components/ui/pagination';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { removeUnderscore } from '@formatters/removeUnderscore.formatter';
import {
  getAllBusinessLinesSchema,
  type GetAllBusinessLinesParams,
} from '@services/businessLines/dtos/getAll.dto';
import { businessLineService } from '@services/businessLines/service';

import { BusinessLineTableCell } from './@components/TableCell';

function businessLinesQueryOptions(params: GetAllBusinessLinesParams) {
  return queryOptions({
    staleTime: EStaleTime.OneHour,
    queryKey: queryKeys.businessLines.list(params),
    queryFn: () => businessLineService.getAll(params),
  });
}

export const Route = createFileRoute('/_layout/(private)/business-lines/')({
  component: BusinessLines,
  validateSearch: search => getAllBusinessLinesSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const options = businessLinesQueryOptions(deps);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingMs: 300,
  pendingMinMs: 600,
});

function BusinessLines() {
  const searchParams = Route.useSearch();

  const { data } = useSuspenseQuery(businessLinesQueryOptions(searchParams));

  return (
    <div className="relative">
      <div
        className="bg-background dark:border-primary-light-one mb-2 grid w-full overflow-hidden rounded-xl border border-zinc-200 shadow"
        role="table"
      >
        <div
          className="bg-primary-light-two dark:text-primary-dark-one grid h-12 grid-cols-6 items-center font-medium text-zinc-600"
          role="row"
        >
          {headers.map(header => (
            <span
              className="text-center text-sm font-medium"
              key={header.accessorKey}
              role="columnheader"
            >
              {header.title}
            </span>
          ))}
        </div>

        {data.list.map(row => (
          <Link
            className="bg-row-odd even:bg-row-even hover:bg-row-odd-hover even:hover:bg-row-even-hover grid min-h-14 grid-cols-6 items-center justify-between transition-all duration-200"
            key={row.id}
            params={{ id: row.id }}
            role="row"
            to="/business-lines/$id"
          >
            <BusinessLineTableCell type="id" value={row.displayId} />
            <BusinessLineTableCell type="date" value={row.createdAt} />
            <BusinessLineTableCell
              value={removeUnderscore(row.name, { capitalize: true })}
            />
            <BusinessLineTableCell value={row.allocationMode} />
            <BusinessLineTableCell
              value={removeUnderscore(row.workflow?.name, { capitalize: true })}
            />
            <BusinessLineTableCell type="tag" value={row.status} />
          </Link>
        ))}

        <EmptyList
          alt="No business lines animation/image"
          description="Click the + button to create a business line"
          isVisible={!data.list.length}
          title="No business lines yet."
        />
      </div>
      <div className="ml-auto flex w-fit items-center">
        <span className="mr-4 text-sm text-gray-500 dark:text-gray-300">
          Total: {data.count || 0} business line(s)
        </span>
        <Pagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
        />
      </div>

      <Anchor
        className="size-12"
        float
        rounded="full"
        size="icon"
        to="/business-lines/create"
        variant="primary"
      >
        <Plus size={24} />
      </Anchor>
    </div>
  );
}

const headers = [
  { title: 'ID', accessorKey: 'displayId' },
  { title: 'Creation date', accessorKey: 'createdAt' },
  { title: 'Name', accessorKey: 'name' },
  { title: 'Allocation', accessorKey: 'allocationMode' },
  { title: 'Workflow', accessorKey: 'workflow.name' },
  { title: 'Status', accessorKey: 'status' },
];
